import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  menuService,
  roleMenuService,
  roleService,
  type Menu,
  type Role,
  type RoleMenu,
} from '@kss/api';
import { ADMIN_ROLE_NAMES, useAuth } from '@kss/auth';
import { setMenuError, setMenuTree, type MenuSliceState } from '../menuSlice';

/**
 * Sidebar node — already nested for direct rendering.
 *
 * `id` mirrors `Menu.menuId` so the consumer can keep a stable selection
 * across reloads. `children` is omitted when the node has no descendants.
 */
export interface SidebarNode {
  id: string;
  name: string;
  icon?: string;
  path?: string;
  children?: SidebarNode[];
}

export interface DynamicMenuResult {
  /** Tree filtered by the current user's role; empty array on first render. */
  nodes: SidebarNode[];
  loading: boolean;
  error: string | null;
  /**
   * True when the current user's role name is on the seeded admin
   * allowlist (`ADMIN_ROLE_NAMES`) — mirrors the bypass in
   * `useHasRole` / `useHasPermission` so the sidebar, permission
   * checks, and role checks all agree on who counts as "admin". Admin
   * users see every active menu regardless of RoleMenu grants and
   * fall back to the static tree if the API returns nothing.
   */
  isAdmin: boolean;
  /**
   * True when the current NON-admin user has at least one active
   * RoleMenu grant that matched an active Menu. Consumers use this to
   * decide whether to render an "empty state" message instead of
   * falling back to the static nav tree.
   *
   * Always `true` for admin users (they don't need a grant).
   */
  hasMenusAssigned: boolean;
}

interface CacheEntry {
  byRole: Map<string, { nodes: SidebarNode[]; hasMenusAssigned: boolean }>;
  loadedAt: number;
}

type RootWithMenu = { menu?: MenuSliceState };

let inFlight: Promise<{ menus: Menu[]; joins: RoleMenu[]; roles: Role[] }> | null = null;
let cache: CacheEntry | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Bump counter — every mounted `useDynamicMenu` subscribes to it and
 * refetches when it changes. `refreshDynamicMenu` increments the
 * counter so a matrix save (which flips grants for the current user's
 * role without changing the role name) refreshes the sidebar live
 * without waiting for the 5-minute TTL or a full re-mount.
 */
let refreshTick = 0;
const refreshSubscribers = new Set<() => void>();
function notifyRefresh(): void {
  refreshTick += 1;
  for (const cb of refreshSubscribers) cb();
}

async function loadAll(): Promise<{ menus: Menu[]; joins: RoleMenu[]; roles: Role[] }> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const [menus, joins, roles] = await Promise.all([
      menuService.all().catch(() => [] as Menu[]),
      roleMenuService.all().catch(() => [] as RoleMenu[]),
      roleService.all().catch(() => [] as Role[]),
    ]);
    cache = { byRole: new Map(), loadedAt: Date.now() };
    return { menus: menus ?? [], joins: joins ?? [], roles: roles ?? [] };
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Build the nested sidebar tree from the flat menu list.
 *
 * When `allowedIds` is `null`, every active menu is included (admin
 * bypass). When it's a `Set`, only menus whose id is in the set are
 * included — an empty Set correctly yields an empty tree instead of
 * the previous "size === 0 → show all" surprise, which let non-admin
 * users with no grants silently see the entire nav.
 *
 * Ordering respects `Menu.SortOrder` first, falling back to a case-
 * insensitive name compare, so tenant admins can reorder the sidebar
 * from the Menus admin UI without a code change.
 *
 * Nesting respects `Menu.ParentId` recursively, so Sales → All Orders
 * → New Order render as expected.
 */
function buildTree(menus: Menu[], allowedIds: Set<string> | null): SidebarNode[] {
  const byParent = new Map<string | undefined, Menu[]>();
  for (const m of menus) {
    if (!m.menuId || m.isActive === false) continue;
    if (allowedIds && !allowedIds.has(m.menuId)) continue;
    const arr = byParent.get(m.parentId) ?? [];
    arr.push(m);
    byParent.set(m.parentId, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => {
      const sa = a.sortOrder ?? 0;
      const sb = b.sortOrder ?? 0;
      if (sa !== sb) return sa - sb;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }
  function nodesOf(parentId: string | undefined): SidebarNode[] {
    const arr = byParent.get(parentId);
    if (!arr) return [];
    return arr.map((m) => {
      const kids = nodesOf(m.menuId);
      const node: SidebarNode = {
        id: String(m.menuId),
        name: m.name,
        icon: m.icon,
        path: m.route ?? m.path,
      };
      if (kids.length > 0) node.children = kids;
      return node;
    });
  }
  return nodesOf(undefined);
}

/**
 * Reset cache and force every mounted `useDynamicMenu` to refetch.
 * Call after saving a RoleMenu edit or after a tenant switch so the
 * sidebar reflects the new grants without waiting for the TTL.
 */
export function refreshDynamicMenu(): void {
  cache = null;
  inFlight = null;
  notifyRefresh();
}

/**
 * Loads the tenant's full menu tree and role-menu join rows once per
 * 5 minutes, then trims the tree down to the menus the current user's
 * role is granted.
 *
 * Admin bypass
 * ------------
 * The role name is matched EXACTLY against `ADMIN_ROLE_NAMES` (the
 * seeded set: Administrator / SuperAdmin / TenantAdmin / Owner) so
 * that a future tenant-defined role like `NonAdmin` or `AdminAssistant`
 * doesn't silently gain full access — this is the same allowlist
 * `useHasRole` / `useHasPermission` use, so all three stay in sync.
 * Admins see every active menu regardless of RoleMenu rows.
 *
 * Non-admin fallback
 * ------------------
 * When a non-admin role has zero matching RoleMenu grants (or the
 * role name doesn't match any known Role in the tenant), the hook
 * returns an empty array and sets `hasMenusAssigned: false`. The
 * PanelLayout uses that flag to render an empty-state message
 * instead of the static config — non-admins should NEVER see the
 * static tree, because it would surface menus they aren't authorised
 * to visit.
 *
 * Re-fetching on role change
 * --------------------------
 * The effect depends on both the user's role name AND user id. When
 * an admin flips a user's role while they're logged in and the
 * session-bootstrap poll refreshes `user`, the hook re-runs and
 * rebuilds the tree to match the new grants.
 */
export function useDynamicMenu(): DynamicMenuResult {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const role = String(user?.role ?? '').trim().toLowerCase();
  const isAdmin = role.length > 0 && ADMIN_ROLE_NAMES.has(role);
  const userId = user?.id ?? '';

  // Bootstrap state from the shared Redux cache — a second consumer
  // (or a re-mount) reads the tree immediately without waiting on the
  // fetch to resolve again.
  const cached = useSelector((s: RootWithMenu) => s.menu);
  const initialNodes =
    cached && cached.cachedForRole === role ? cached.nodes : [];

  const [nodes, setNodes] = useState<SidebarNode[]>(initialNodes);
  const [hasMenusAssigned, setHasMenusAssigned] = useState<boolean>(
    cached && cached.cachedForRole === role ? cached.hasMenusAssigned : isAdmin,
  );
  const [loading, setLoading] = useState<boolean>(initialNodes.length === 0);
  const [error, setError] = useState<string | null>(null);
  // Local echo of `refreshTick` — bumping it re-runs the effect below,
  // which is how `refreshDynamicMenu()` propagates to a mounted hook.
  const [tick, setTick] = useState<number>(refreshTick);
  useEffect(() => {
    const cb = () => setTick(refreshTick);
    refreshSubscribers.add(cb);
    return () => {
      refreshSubscribers.delete(cb);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // No user (logged out) — clear + short-circuit.
    if (!userId || !role) {
      setNodes([]);
      setHasMenusAssigned(false);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const roleCache = cache?.byRole.get(role);
    if (roleCache && cache && Date.now() - cache.loadedAt < CACHE_TTL_MS) {
      setNodes(roleCache.nodes);
      setHasMenusAssigned(roleCache.hasMenusAssigned);
      setLoading(false);
      dispatch(
        setMenuTree({
          nodes: roleCache.nodes,
          isAdmin,
          hasMenusAssigned: roleCache.hasMenusAssigned,
          role,
        }),
      );
      return;
    }

    (async () => {
      try {
        const { menus, joins, roles } = await loadAll();
        if (cancelled) return;

        let tree: SidebarNode[];
        let assigned: boolean;

        if (isAdmin) {
          // Admin bypass: every active menu, no RoleMenu filter.
          tree = buildTree(menus, null);
          assigned = true;
        } else {
          // Map the JWT role NAME ("Manager") to a roleId — the live
          // RoleMenu rows reference roleId, not the role name string.
          const roleId = roles.find(
            (r) => String(r.name ?? '').trim().toLowerCase() === role,
          )?.roleId;

          const allowed = new Set<string>();
          if (roleId) {
            for (const j of joins) {
              if (j.isActive === false) continue;
              if (j.roleId === roleId && j.menuId) allowed.add(j.menuId);
            }
          }

          // Strict allowlist — an empty set yields an empty tree. This
          // deliberately does NOT fall through to "show all menus" for
          // unknown roles or ungranted users, which the previous
          // implementation did via a `size > 0` size-guard.
          tree = buildTree(menus, allowed);
          assigned = tree.length > 0;
        }

        cache?.byRole.set(role, { nodes: tree, hasMenusAssigned: assigned });
        setNodes(tree);
        setHasMenusAssigned(assigned);
        dispatch(
          setMenuTree({ nodes: tree, isAdmin, hasMenusAssigned: assigned, role }),
        );
      } catch (e) {
        if (!cancelled) {
          const msg = (e as Error)?.message ?? 'Failed to load menus';
          setError(msg);
          dispatch(setMenuError(msg));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `userId` is included so a login / logout / user-switch invalidates
    // the local component state even if the role name happens to match.
    // `tick` re-runs the effect when `refreshDynamicMenu()` is called
    // — e.g. right after a RoleMenu save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, userId, isAdmin, tick]);

  return { nodes, loading, error, isAdmin, hasMenusAssigned };
}
